/**
 * Class to manage scroll-related actions.
 */
export default class ScrollManager {
  /**
   * Scrolls to a specific year in FeDatePicker.
   * @param elementId - The ID of the element to scroll to.
   * @param offset - The offset to apply when scrolling.
   */
  scrollToYear(elementId: string, offset: number): void {
    const element: HTMLElement | null = document.getElementById(elementId);

    if (element) {
      // Scroll the parent node to the position of the element minus some offset
      (element.parentNode as HTMLElement).scrollTop = element.offsetTop - (element.parentNode as HTMLElement).offsetTop - element.scrollHeight * 3;
    }
  }

  /**
   * Sets the scroll position of the element's container to the position of the element with the given element ID.
   * @param elementId - The ID of the element to scroll to.
   */
  scrollToListItem(elementId: string): void {
    const element: HTMLElement | null = document.getElementById(elementId);
    if (element) {
      const parent: HTMLElement | null = element.parentElement;
      if (parent) {
        // Set the parent's scrollTop to the element's offsetTop
        parent.scrollTop = element.offsetTop;
      }
    }
  }

  /**
   * Scrolls to the selected element. Default is documentElement (i.e., html element).
   * @param selector - The CSS selector of the element to scroll to.
   * @param left - The left scroll position.
   * @param top - The top scroll position.
   * @param behavior - The scroll behavior ('auto' or 'smooth').
   */
  scrollTo(selector: string, left: number, top: number, behavior: ScrollBehavior): void {
    console.log("scrollTo", selector, left, top, behavior);
    const element: Element | HTMLElement = document.querySelector(selector) || document.documentElement;
    element.scrollTo({ left, top, behavior });
  }

  /**
   * Scrolls the provided selector into view.
   * @param selector - The CSS selector of the element to scroll into view.
   * @param behavior - The scroll behavior ('auto' or 'smooth').
   */
  scrollIntoView(selector: string, behavior: ScrollBehavior): void {
    const element: Element | HTMLElement = document.querySelector(selector) || document.documentElement;
    if (element) {
      element.scrollIntoView({ behavior, block: 'center', inline: 'start' });
    }
  }

  /**
   * Scrolls to the bottom of the provided selector.
   * @param selector - The CSS selector of the element to scroll to the bottom of.
   * @param behavior - The scroll behavior ('auto' or 'smooth').
   */
  scrollToBottom(selector: string, behavior: ScrollBehavior): void {
    const element: Element | null = document.querySelector(selector);
    if (element) {
      element.scrollTo({
        top: element.scrollHeight,
        behavior: behavior
      });
    } else {
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: behavior
      });
    }
  }

  /**
   * Locks the scroll of the selected element. Default is body.
   * @param selector - The CSS selector of the element to lock scroll on.
   * @param lockclass - The class to add to lock the scroll.
   */
  lockScroll(selector: string, lockclass: string): void {
    const element: Element | HTMLElement = document.querySelector(selector) || document.body;

    // Check if the body has a scroll bar
    const hasScrollBar: boolean = window.innerWidth > document.body.clientWidth;

    if (hasScrollBar) {
      element.classList.add(lockclass);
    } else {
      const lockClassNoPadding: string = lockclass + "-no-padding";
      element.classList.add(lockClassNoPadding);
    }
  }

  /**
   * Unlocks the scroll. Default is body.
   * @param selector - The CSS selector of the element to unlock scroll on.
   * @param lockclass - The class to remove to unlock the scroll.
   */
  unlockScroll(selector: string, lockclass: string): void {
    const element: Element | HTMLElement = document.querySelector(selector) || document.body;

    // Remove both lock classes to be sure it's unlocked
    element.classList.remove(lockclass);
    element.classList.remove(lockclass + "-no-padding");
  }
}
